/**
 * Converts a camelCase or PascalCase key to a human-readable capitalised label.
 * Examples:
 *   primaryColor       → Primary Color
 *   enableTextSelection → Enable Text Selection
 *   APIKey             → API Key
 *   logoUrl            → Logo Url
 */
export const labelFromKey = (key: string): string => {
  return key
    // Insert space before an uppercase letter preceded by a lowercase letter
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Insert space before an uppercase letter that starts a new word after a run of caps (e.g. APIKey → API Key)
    .replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1 $2')
    // Replace underscores and hyphens with spaces
    .replace(/[_-]/g, ' ')
    // Capitalise the very first character
    .replace(/^./, (s) => s.toUpperCase())
    // Collapse any double spaces
    .replace(/\s{2,}/g, ' ')
    .trim();
};

export const toTitleCase = (str: string | null | undefined): string => {
  if (!str) {
    return '';
  }

  return str.toLowerCase().split(' ').map((word: string) => {
    if (word.length === 0) return word;

    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
};

export const stringToDateArray = (range: string): Date[] => {
  let startDate: Date | undefined = undefined;
  const today = new Date();
  if (range === 'last_7_days') {
    startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
  } else if (range === 'last_15_days') {
    startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 15);
  } else if (range === 'last_30_days') {
    startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
  } else if (range === 'today') {
    startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  } else {
    startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  return [startDate, today];
};

/** The trimmed value when it starts with http(s):// and has no spaces, otherwise null. It doesn't check that the host is complete. */
export const httpUrlOrNull = (value: string): string | null => {
  const url = value.trim();
  return /^https?:\/\/\S+$/i.test(url) ? url : null;
};
